/**
 * Checklist — do/don't list with ✓ (compatible) / ✗ (incompatible) markers
 * (CX-2728 "Designed to Last"). Compatible items may carry a `url` linking to a
 * color-finder search; those rows render a `→` arrow and open the link.
 *
 * `url` is expected to be a relative in-app path (e.g. "/search?..."), resolved
 * against the current origin so the link stays environment-aware (QA → QA,
 * prod → prod) — same pattern as ContentLink.
 */

import type { ChecklistItem } from "../chapter.types";
import "./Checklist.css";

const resolveUrl = (url: string) =>
  url.startsWith("/") ? `${window.location.origin}${url}` : url;

interface ChecklistProps {
  items: ChecklistItem[];
}

const ArrowIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    className="cmf-checklist__arrow"
  >
    <path
      d="M2.5 6h7M6.5 3 9.5 6l-3 3"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ItemBody = ({ item }: { item: ChecklistItem }) => (
  <>
    <span
      className={`cmf-checklist__mark cmf-checklist__mark--${item.ok ? "ok" : "no"}`}
      aria-hidden="true"
    >
      {item.ok ? "✓" : "✗"}
    </span>
    <span className="cmf-checklist__label">
      {item.label}
      {item.url && <ArrowIcon />}
    </span>
  </>
);

export const Checklist = ({ items }: ChecklistProps) => (
  <ul className="cmf-checklist">
    {items.map((item) => (
      <li key={item.label} className="cmf-checklist__item">
        {item.url ? (
          <a
            className={`cmf-checklist__row cmf-checklist__row--${item.ok ? "ok" : "no"} cmf-checklist__row--link`}
            href={resolveUrl(item.url)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ItemBody item={item} />
          </a>
        ) : (
          <div
            className={`cmf-checklist__row cmf-checklist__row--${item.ok ? "ok" : "no"}`}
          >
            <ItemBody item={item} />
          </div>
        )}
      </li>
    ))}
  </ul>
);
